/**
 * Felix Async Form — <felix-async-form>
 *
 * Submit transport only: wraps a real <form action method="post"> and posts it
 * via fetch instead of navigation, so third-party endpoints (e.g. Iterable's
 * publicAddSubscriberForm) never get to render their own confirmation page.
 * Fields inside are whatever the host block composes (form-input components);
 * this element doesn't know or care what they are.
 *
 * The cross-origin POST runs in no-cors mode, so the response is opaque:
 * success is optimistic (same trade-off as the Webflow embeds this replaces).
 * The [data-form-error] region is only reachable on a genuine network failure.
 *
 * Markup contract:
 *   <felix-async-form data-success-event="…" data-page-name="…">
 *     <form action="…" method="post">…<button type="submit">…</button></form>
 *     <p data-form-success data-message="…" hidden></p>
 *     <p data-form-error data-message="…" hidden></p>
 *   </felix-async-form>
 *
 * data-success-event fires through window.felixTrack once the post has gone out.
 * It deliberately does NOT go on the submit button as data-track-event:
 * tracking.js delegates that on click, which fires before validation and would
 * count blocked attempts as conversions. A honeypot hit never reaches it either.
 *
 * data-page-name is passed explicitly because tracking.js reads page context from
 * <body data-page-name>, which nothing in the theme currently sets. An explicit
 * value wins over that lookup, so this keeps working either way.
 *
 * The success event carries an attribution snapshot (shared/attribution.js): the
 * path, a hash of the submitted address, and every ad identifier the browser can
 * still see. Nothing is persisted to build it, so UTMs and the referrer report
 * null once the visitor has navigated away from the URL that carried them.
 *
 * page_location is still not sent — GTM built-ins, GA4's page_location and
 * Segment's context.page all resolve the full URL at fire time. `path` is sent
 * anyway, deliberately: it makes this payload diffable against the equivalent
 * event charm fires, and it survives into destinations that drop page context.
 *
 * Host gotcha: success hides the form via the `hidden` attribute, whose UA
 * display:none LOSES to any display utility (.flex etc.) on the <form>. Hosts
 * using one must add `.their-form[hidden] { display: none; }` to their block CSS.
 */

import { collectAttribution, getFormName, hashEmail } from '../shared/attribution.js'

class FelixAsyncForm extends HTMLElement {
	connectedCallback() {
		this.form = this.querySelector('form')
		if (!this.form || !this.form.action) return

		this.success = this.querySelector('[data-form-success]')
		this.error = this.querySelector('[data-form-error]')

		// Inject the message on reveal (not pre-rendered) so the status region
		// registers a content mutation and actually announces.
		this.reveal = (region) => {
			if (!region) return
			region.textContent = region.dataset.message || ''
			region.hidden = false
		}

		this.onSubmit = async (event) => {
			event.preventDefault()

			if (!this.form.reportValidity()) return

			// Honeypot: bots fill hidden fields, people can't see them. Show fake
			// success rather than silently ignoring, so bots don't retry.
			const honeypot = this.form.querySelector('[name="felix_hp"]')
			if (honeypot && honeypot.value) {
				this.form.hidden = true
				this.reveal(this.success)
				return
			}

			const submitButton = this.form.querySelector('[type="submit"]')
			if (submitButton) submitButton.disabled = true
			if (this.error) this.error.hidden = true
			this.form.setAttribute('aria-busy', 'true')

			const body = new FormData(this.form)
			body.delete('felix_hp')

			try {
				// URLSearchParams → application/x-www-form-urlencoded, matching the
				// native form post this replaces (FormData would send multipart).
				await fetch(this.form.action, { method: 'POST', body: new URLSearchParams(body), mode: 'no-cors' })
				const successEvent = this.dataset.successEvent
				if (successEvent) {
					const props = collectAttribution()

					const pageName = this.dataset.pageName
					if (pageName) props.pageName = pageName

					const formName = getFormName(this.form.action)
					if (formName) props.formName = formName

					// Matched on type, not name, so any host block's email field works.
					const emailHash = await hashEmail(this.form.querySelector('input[type="email"]')?.value)
					if (emailHash) props.emailHash = emailHash

					window.felixTrack?.(successEvent, props)
				}
				this.form.hidden = true
				this.reveal(this.success)
				// Focus would otherwise strand inside the hidden form.
				this.success?.focus()
			} catch {
				this.reveal(this.error)
				if (submitButton) submitButton.disabled = false
			} finally {
				this.form.removeAttribute('aria-busy')
			}
		}

		this.form.addEventListener('submit', this.onSubmit)
	}

	disconnectedCallback() {
		this.form?.removeEventListener('submit', this.onSubmit)
	}
}

if (!customElements.get('felix-async-form')) {
	customElements.define('felix-async-form', FelixAsyncForm)
}
